import { X } from 'lucide-react';
import { type FieldFilter, describeFilter, makeFieldFilter } from '../utils/payloadQuery';

interface Props {
  filters: readonly FieldFilter[];
  onRemove: (id: string) => void;
  /** Replaces a chip with its inverse — the only way to reach "is not", and
   *  it's reachable by clicking the thing you already made. */
  onReplace: (oldId: string, next: FieldFilter) => void;
  onClear: () => void;
  /** Rows currently shown / rows in the buffer. */
  matched: number;
  total: number;
  /** True when search text alone is narrowing (no chips). */
  textActive: boolean;
}

export function FilterChips({
  filters,
  onRemove,
  onReplace,
  onClear,
  matched,
  total,
  textActive,
}: Props) {
  if (filters.length === 0 && !textActive) return null;

  const invert = (f: FieldFilter) =>
    onReplace(f.id, makeFieldFilter(f.path, f.op === 'is' ? 'not' : 'is', f.value));

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-(--color-border) bg-(--color-surface) px-2 py-[5px]">
      {filters.map((f) => (
        <span
          key={f.id}
          className="inline-flex items-center overflow-hidden rounded border border-(--color-green) bg-(--color-green-bg)"
        >
          <button
            type="button"
            onClick={() => invert(f)}
            title={f.op === 'is' ? 'Click to exclude instead' : 'Click to include instead'}
            className="cursor-pointer px-[6px] py-[1px] font-mono text-[0.731em] text-(--color-green)"
          >
            {describeFilter(f)}
          </button>
          <button
            type="button"
            onClick={() => onRemove(f.id)}
            aria-label={`Remove filter ${describeFilter(f)}`}
            className="cursor-pointer px-[3px] py-[2px] text-(--color-green) opacity-70 hover:opacity-100"
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <span className="ml-auto flex items-center gap-2 pl-1 font-mono text-[0.692em] text-(--color-text3)">
        <span>
          {matched.toLocaleString()} of {total.toLocaleString()}
        </span>
        {filters.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="cursor-pointer font-sans font-bold text-(--color-text3) underline-offset-2 hover:text-(--color-text) hover:underline"
          >
            Clear
          </button>
        )}
      </span>
    </div>
  );
}
